import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Loader2, CheckCircle2, ArrowRight, ArrowLeft, ExternalLink, Info, ShieldCheck } from "lucide-react";

const STEPS = [
  { id: "business", title: "Meta Business Manager" },
  { id: "waba", title: "WABA ID" },
  { id: "phone", title: "Número WhatsApp" },
  { id: "tokens", title: "Credenciais técnicas" },
];

/**
 * Multi-step wizard for tenant admins to manually configure their WhatsApp
 * Business connection. Used as the fallback when Meta Embedded Signup is not
 * approved on the platform side, or when the admin prefers manual setup.
 *
 * On finish → POST /api/admin/organizations/{org_id}/whatsapp/manual-setup → status='connected'.
 * This wizard is now reserved to superadmin (Phase 2 P2 Session A).
 */
export default function WhatsappSetupWizard({ onClose, onDone, orgId }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    business_id: "",
    waba_id: "",
    phone_number_id: "",
    display_phone_number: "",
    verified_name: "",
    access_token: "",
    app_secret: "",
    app_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const canNext = () => {
    if (step === 1) return form.waba_id.trim().length > 0;
    if (step === 2) return form.phone_number_id.trim().length > 0;
    if (step === 3) return form.access_token.trim().length > 10;
    return true;
  };

  const submit = async () => {
    setSaving(true); setError("");
    try {
      const url = orgId ? `/admin/organizations/${orgId}/whatsapp/manual-setup` : "/organizations/me/whatsapp/manual-setup";
      await api.post(url, form);
      onDone();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erro ao guardar.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="wa-wizard-modal" className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-display font-semibold text-zinc-900">Configurar WhatsApp Business</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Passo {step + 1} de {STEPS.length} · {STEPS[step].title}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pt-3">
          <div className="flex gap-1">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-emerald-600" : "bg-zinc-200"}`} />
            ))}
          </div>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div data-testid="wa-wizard-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          {step === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-700">
                Precisas primeiro de ter uma conta <strong>Meta Business Manager</strong> criada e verificada com os dados da tua agência.
              </p>
              <a href="https://business.facebook.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50">
                Abrir Meta Business Manager <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <div className="space-y-1.5 pt-2">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Business ID (opcional agora)</label>
                <input value={form.business_id} onChange={(e) => set("business_id", e.target.value)} className="form-input" placeholder="ex: 123456789012345" />
                <p className="text-xs text-zinc-500">Business Settings → Business Info → Business Manager ID.</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5" />
                Confirma que a tua empresa está <strong>Business Verified</strong> antes de continuar. Sem verification a Meta bloqueia envios.
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-700">Dentro do Business Manager, vai a <strong>WhatsApp Accounts</strong> e escolhe (ou cria) a WABA da agência.</p>
              <div className="space-y-1.5">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">WABA ID <span className="text-red-600">*</span></label>
                <input data-testid="wa-wizard-waba" required value={form.waba_id} onChange={(e) => set("waba_id", e.target.value)} className="form-input" placeholder="ex: 987654321098765" autoFocus />
                <p className="text-xs text-zinc-500">WhatsApp Manager → Account Info → WhatsApp Business Account ID.</p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-700">Escolhe o número WhatsApp que a agência vai usar. Não pode estar registado no WhatsApp normal ou WhatsApp Business "de consumidor".</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Phone Number ID <span className="text-red-600">*</span></label>
                  <input data-testid="wa-wizard-phone-id" required value={form.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} className="form-input" placeholder="ex: 15551234567" autoFocus />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Número visível</label>
                  <input data-testid="wa-wizard-display-phone" value={form.display_phone_number} onChange={(e) => set("display_phone_number", e.target.value)} className="form-input" placeholder="+351911111111" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Verified Name</label>
                  <input data-testid="wa-wizard-verified-name" value={form.verified_name} onChange={(e) => set("verified_name", e.target.value)} className="form-input" placeholder="Nome que os clientes vêem no WhatsApp" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 mt-0.5" />
                Os tokens são guardados <strong>encriptados</strong> e nunca são devolvidos por endpoints públicos. Nem a Do IT Happen os consegue ler em texto claro no painel.
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Access Token (System User) <span className="text-red-600">*</span></label>
                <input data-testid="wa-wizard-access-token" required type="password" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} className="form-input font-mono text-xs" placeholder="EAA..." />
                <p className="text-xs text-zinc-500">Meta Business Suite → System Users → Generate Token com permissões whatsapp_business_management + whatsapp_business_messaging.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">App ID</label>
                  <input data-testid="wa-wizard-app-id" value={form.app_id} onChange={(e) => set("app_id", e.target.value)} className="form-input" placeholder="opcional" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium">App Secret</label>
                  <input data-testid="wa-wizard-app-secret" type="password" value={form.app_secret} onChange={(e) => set("app_secret", e.target.value)} className="form-input font-mono text-xs" placeholder="Meta App Dashboard → Definições → Básico" />
                  <p className="text-[10px] text-amber-700 leading-snug">Necessário para <strong>receber respostas na Inbox</strong>. Sem o App Secret correto, a Meta assina os webhooks mas nós não conseguimos validá-los e as mensagens recebidas não aparecem.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-100 flex justify-between items-center gap-2">
          <button
            type="button"
            onClick={() => step === 0 ? onClose() : setStep(step - 1)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50"
            data-testid="wa-wizard-prev"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {step === 0 ? "Cancelar" : "Anterior"}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              data-testid="wa-wizard-next"
              className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
            >
              Continuar <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canNext() || saving}
              data-testid="wa-wizard-submit"
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {saving ? "A guardar…" : "Ligar WhatsApp"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
